import React, { useEffect, useRef, useState } from 'react';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ReportDA } from '../../../../services/admin/report.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { BalanceSheet } from '../../../../models/report.model';
import { FiscalYearLike, resolvePreviousFiscalYear } from '../../../../scripts/fiscal.year.previous.script';
import { formatMoney, amountClass, tabularStyle } from '../../../../scripts/format.value.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { dateFormatLocalized } from '../../../../scripts/date.time.format.script';
import { triggerBlobDownload } from '../../../../scripts/export.blob.script';
import { exportFallbackFilename } from '../../../../scripts/document.filename.script';
import { GlobalSwal } from '../../../../styles/swal.style';

import AppPreloader from '../../../../components/Preloader';
import AppReportTree from '../../../../components/element/ReportTree';
import DropdownActions from '../../../../components/dropdown/DropdownActions';

export interface BalanceSheetPageProps {};

const BalanceSheetPage: React.FunctionComponent<BalanceSheetPageProps> = props => {
  const {setRoute, companyForUser, accountingAccountsForCompany} = UseOutletContextAccounting()
  const {lang} = StateLanguage()

  const [asOfDate, setAsOfDate] = useState<string>('')
  const [fetchDate, setFetchDate] = useState<string>('')
  const [report, setReport] = useState<BalanceSheet | null>(null)
  const [reportPrev, setReportPrev] = useState<BalanceSheet | null>(null)
  const [fiscalYears, setFiscalYears] = useState<FiscalYearLike[]>([])
  const [previousDate, setPreviousDate] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'columns'>('list')
  const [showZero, setShowZero] = useState<boolean>(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const fiscalYearsRef = useRef<FiscalYearLike[]>([])

  const locale = (lang.code ?? 'fr').substring(0, 2).toLowerCase()
  const comparisonLabel = previousDate ? dateFormatLocalized(lang.code, previousDate) : ''

  useEffect(() => {
    setRoute && setRoute({path: {root: lang.labels.accounting, branch: lang.labels.balanceSheet}, company: false})
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser) {
      loadFiscalYearsAndInit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    if (companyForUser && fetchDate && fiscalYearsRef.current.length > 0) {
      loadReport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDate])

  const loadFiscalYearsAndInit = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id) return

    try {
      const response = await FiscalYearDA.getFiscalYears(id)
      if (response.status === 200) {
        const data = response.data
        const items: FiscalYearLike[] = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? []
        setFiscalYears(items)
        fiscalYearsRef.current = items

        if (items.length > 0) {
          const today = new Date()
          const current = items.find(fy => new Date(fy.start_date) <= today && new Date(fy.end_date) >= today)
            ?? items.sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())[0]

          const dateStr = current.end_date.substring(0, 10)
          setAsOfDate(dateStr)
          setFetchDate(dateStr)
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const loadReport = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id || !fetchDate) return

    setLoading(true)
    setReport(null)
    setReportPrev(null)
    setPreviousDate('')

    try {
      const response = await ReportDA.getBalanceSheet(id, fetchDate)
      if (response.status === 200) {
        setReport(response.data as BalanceSheet)
      } else {
        setReport(null)
      }

      // Find the FY containing the report date, then resolve N-1
      const fys = fiscalYearsRef.current
      if (fys.length > 0) {
        const reportFY = fys.find(fy =>
          fy.start_date.substring(0, 10) <= fetchDate && fy.end_date.substring(0, 10) >= fetchDate
        )
        if (reportFY) {
          const prev = resolvePreviousFiscalYear(fys, { start_date: reportFY.start_date })
          if (prev) {
            const prevDate = prev.end_date.substring(0, 10)
            setPreviousDate(prevDate)
            const prevResponse = await ReportDA.getBalanceSheet(id, prevDate)
            if (prevResponse.status === 200) {
              setReportPrev(prevResponse.data as BalanceSheet)
            }
          }
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoading(false)
    }
  }

  const isValidDateStr = (s: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false
    const [y, m, d] = s.split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d))
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value
    setAsOfDate(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (isValidDateStr(val)) {
      debounceRef.current = setTimeout(() => setFetchDate(val), 400)
    }
  }

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id || !fetchDate || exporting) return
    setExporting(true)
    try {
      const fallback = exportFallbackFilename(lang.labels.balanceSheet, companyForUser?.name ?? '', fetchDate, format)
      const result = await ReportDA.exportBalanceSheet(id, format, {
        date: fetchDate,
        previous_date: previousDate || undefined,
        layout: viewMode === 'list' ? 'vertical' : 'horizontal',
        hide_zero: !showZero,
        lang: lang.code.toLowerCase(),
      }, fallback)
      if (result.blob && result.filename) {
        triggerBlobDownload(result.blob, result.filename)
      } else {
        GlobalSwal.fire({ title: result.error || lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
      }
    } catch (error) {
      console.error('[BalanceSheet] export failed', error)
      GlobalSwal.fire({ title: lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
    } finally {
      setExporting(false)
    }
  }

  const resultLabel = report
    ? (report.result_label === 'loss' ? lang.labels.lossOfYear : lang.labels.profitOfYear)
    : ''

  const resultClass = report
    ? (report.result >= 0 ? 'text-success' : 'text-danger')
    : ''

  const prevResultClass = reportPrev
    ? (reportPrev.result >= 0 ? 'text-success' : 'text-danger')
    : ''

  const renderBadge = () => {
    if (!report) return null

    if (report.is_closed) {
      return report.balanced
        ? <span className="badge badge-success fs-7 px-4 py-2">{lang.labels.balanced}</span>
        : <span className="badge badge-danger fs-7 px-4 py-2">
            {lang.labels.notBalanced} ({formatMoney(report.assets.total - report.liabilities.total - report.result)})
          </span>
    }

    return report.balanced
      ? <span className="badge badge-light-success fs-7 px-4 py-2">{lang.labels.balanced}</span>
      : <span className="badge badge-light-danger fs-7 px-4 py-2">
          {lang.labels.notBalanced} ({formatMoney(report.assets.total - report.liabilities.total - report.result)})
        </span>
  }

  const renderResultLine = (isBold: boolean) => {
    if (!report) return null
    return (
      <div className={`d-flex justify-content-between ${isBold ? 'fw-bolder fs-5' : 'fw-bold fs-6'} mt-3`}>
        <span>{resultLabel}</span>
        <div className="d-flex">
          <span className={`text-end ${resultClass}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.result)}</span>
          { reportPrev &&
            <>
              <span className={`text-end ${prevResultClass}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.result)}</span>
              <span className={`text-end ${amountClass(report.result - reportPrev.result)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                {((report.result - reportPrev.result) >= 0 ? '+' : '')}{formatMoney(report.result - reportPrev.result)}
              </span>
            </>
          }
        </div>
      </div>
    )
  }

  const renderTotalLiabilitiesAndResult = () => {
    if (!report) return null
    const total = report.liabilities.total + report.result
    const prevTotal = reportPrev ? reportPrev.liabilities.total + reportPrev.result : null
    return (
      <div className="d-flex justify-content-between fw-bolder fs-4 mt-3 border-top pt-3">
        <span>{lang.labels.totalLiabilitiesAndResult}</span>
        <div className="d-flex">
          <span className={`text-end ${amountClass(total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(total)}</span>
          { prevTotal !== null &&
            <>
              <span className={`text-end ${amountClass(prevTotal)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(prevTotal)}</span>
              <span className={`text-end ${amountClass(total - prevTotal)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                {((total - prevTotal) >= 0 ? '+' : '')}{formatMoney(total - prevTotal)}
              </span>
            </>
          }
        </div>
      </div>
    )
  }

  const renderListView = () => {
    if (!report) return null
    return (
      <>
        <div className="card mb-5">
          <div className="card-header">
            <div className="card-title">
              <h4>{lang.labels.assets}</h4>
            </div>
          </div>
          <div className="card-body">
            <AppReportTree
              groups={report.assets.groups}
              groupsPrevious={reportPrev?.assets?.groups}
              locale={locale}
              showZero={showZero}
              accounts={accountingAccountsForCompany}
              previousLabel={comparisonLabel}
            />
            <div className="border-top mt-3 pt-3"></div>
            <div className="d-flex justify-content-between fw-bolder fs-5">
              <span>{lang.labels.total} {lang.labels.assets}</span>
              <div className="d-flex">
                <span className={`text-end ${amountClass(report.assets.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.assets.total)}</span>
                { reportPrev &&
                  <>
                    <span className={`text-end ${amountClass(reportPrev.assets.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.assets.total)}</span>
                    <span className={`text-end ${amountClass(report.assets.total - reportPrev.assets.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                      {((report.assets.total - reportPrev.assets.total) >= 0 ? '+' : '')}{formatMoney(report.assets.total - reportPrev.assets.total)}
                    </span>
                  </>
                }
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-5">
          <div className="card-header">
            <div className="card-title">
              <h4>{lang.labels.liabilities}</h4>
            </div>
          </div>
          <div className="card-body">
            <AppReportTree
              groups={report.liabilities.groups}
              groupsPrevious={reportPrev?.liabilities?.groups}
              locale={locale}
              showZero={showZero}
              accounts={accountingAccountsForCompany}
              previousLabel={comparisonLabel}
            />
            <div className="border-top mt-3 pt-3"></div>
            <div className="d-flex justify-content-between fw-bolder fs-5">
              <span>{lang.labels.total} {lang.labels.liabilities}</span>
              <div className="d-flex">
                <span className={`text-end ${amountClass(report.liabilities.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.liabilities.total)}</span>
                { reportPrev &&
                  <>
                    <span className={`text-end ${amountClass(reportPrev.liabilities.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.liabilities.total)}</span>
                    <span className={`text-end ${amountClass(report.liabilities.total - reportPrev.liabilities.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                      {((report.liabilities.total - reportPrev.liabilities.total) >= 0 ? '+' : '')}{formatMoney(report.liabilities.total - reportPrev.liabilities.total)}
                    </span>
                  </>
                }
              </div>
            </div>
            {renderResultLine(true)}
            {renderTotalLiabilitiesAndResult()}
          </div>
        </div>
      </>
    )
  }

  const renderColumnsView = () => {
    if (!report) return null
    return (
      <div className="row">
        <div className="col-lg-6">
          <div className="card mb-5">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.assets}</h4>
              </div>
            </div>
            <div className="card-body">
              <AppReportTree
                groups={report.assets.groups}
                groupsPrevious={reportPrev?.assets?.groups}
                locale={locale}
                showZero={showZero}
                accounts={accountingAccountsForCompany}
                previousLabel={comparisonLabel}
              />
              <div className="border-top mt-3 pt-3"></div>
              <div className="d-flex justify-content-between fw-bolder fs-5">
                <span>{lang.labels.total} {lang.labels.assets}</span>
                <div className="d-flex">
                  <span className={`text-end ${amountClass(report.assets.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.assets.total)}</span>
                  { reportPrev &&
                    <>
                      <span className={`text-end ${amountClass(reportPrev.assets.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.assets.total)}</span>
                      <span className={`text-end ${amountClass(report.assets.total - reportPrev.assets.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                        {((report.assets.total - reportPrev.assets.total) >= 0 ? '+' : '')}{formatMoney(report.assets.total - reportPrev.assets.total)}
                      </span>
                    </>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card mb-5">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.liabilities}</h4>
              </div>
            </div>
            <div className="card-body">
              <AppReportTree
                groups={report.liabilities.groups}
                groupsPrevious={reportPrev?.liabilities?.groups}
                locale={locale}
                showZero={showZero}
                accounts={accountingAccountsForCompany}
                previousLabel={comparisonLabel}
              />
              <div className="border-top mt-3 pt-3"></div>
              <div className="d-flex justify-content-between fw-bolder fs-5">
                <span>{lang.labels.total} {lang.labels.liabilities}</span>
                <div className="d-flex">
                  <span className={`text-end ${amountClass(report.liabilities.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.liabilities.total)}</span>
                  { reportPrev &&
                    <>
                      <span className={`text-end ${amountClass(reportPrev.liabilities.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.liabilities.total)}</span>
                      <span className={`text-end ${amountClass(report.liabilities.total - reportPrev.liabilities.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                        {((report.liabilities.total - reportPrev.liabilities.total) >= 0 ? '+' : '')}{formatMoney(report.liabilities.total - reportPrev.liabilities.total)}
                      </span>
                    </>
                  }
                </div>
              </div>
              {renderResultLine(true)}
              {renderTotalLiabilitiesAndResult()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-100 h-100">
      { companyForUser && asOfDate
        ?
        <>
          <div className="card mb-5">
            <div className="card-header">
              <div className="card-title d-flex align-items-center flex-wrap gap-3">
                <h2 className="mb-0">{lang.labels.balanceSheet}</h2>
                { report?.interim === true
                  ? <span className="badge badge-light-warning fs-7 px-4 py-2">{lang.labels.interim}</span>
                  : report?.provisional === true
                    ? <span className="badge badge-light-warning fs-7 px-4 py-2">{lang.labels.provisional}</span>
                    : null
                }
                {renderBadge()}
              </div>
              <div className="card-toolbar">
                <div className="d-flex align-items-center gap-3">
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setViewMode('list')}
                    >
                      {lang.labels.viewList}
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'columns' ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setViewMode('columns')}
                    >
                      {lang.labels.viewColumns}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    onClick={() => setShowZero(!showZero)}
                  >
                    {showZero ? lang.labels.hideZeroAccounts : lang.labels.showZeroAccounts}
                  </button>
                  <div className="d-flex align-items-center">
                    <label className="fw-bold me-3 text-nowrap">{lang.labels.asOfDate}:</label>
                    <input
                      type="date"
                      className="form-control w-175px"
                      value={asOfDate}
                      onChange={handleDateChange}
                    />
                  </div>
                  <DropdownActions
                    label={exporting ? `${lang.labels.export}...` : lang.labels.export}
                    buttonClassName={`btn btn-sm ${!fetchDate || loading || exporting ? 'btn-light disabled' : 'btn-light btn-active-light-primary'}`}
                    items={[
                      { key: 'xlsx', label: `${lang.labels.excel} (.xlsx)`, onClick: () => handleExport('xlsx') },
                      { key: 'pdf', label: `${lang.labels.pdf} (.pdf)`, onClick: () => handleExport('pdf') },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          { loading
            ?
            <AppPreloader />
            :
            report
            ?
            <>
              { viewMode === 'list' ? renderListView() : renderColumnsView() }
            </>
            :
            <div className="card">
              <div className="card-body text-center text-muted py-10">
                {lang.labels.noRecordsFound}
              </div>
            </div>
          }
        </>
        :
        <AppPreloader />
      }
    </div>
  )
};

export default BalanceSheetPage;
